// @ts-check
/**
 * MazeGenerator - Procedural labyrinth generation for void-gallery
 *
 * Algorithm: Room-based maze with controlled connectivity
 *
 * Requirements:
 * 1. Generates enough rooms to display all paintings (1-3 paintings per room)
 * 2. Each room has 1-3 entrances/exits
 * 3. Maze is solvable (clear path from entrance to exit)
 * 4. Random layout each generation
 *
 * Identity contract:
 * - room.id = `room_{row}_{col}` - a function of grid position only, so it is
 *   stable across generations for a given paintingCount/cellSize regardless of seed.
 * - paintingSlot.id = `{room.id}_{wall}` - deterministic under a fixed seed. Which
 *   wall (and therefore which room) ends up holding slot N depends on the seeded
 *   shuffle in getAvailableWalls(), so the same seed always reproduces the same
 *   set of slot ids in the same order; a different seed can produce a different set.
 * - The `paintingSlots` array returned by generate() and the `paintings` array on
 *   each returned room hold the *same* slot object references (not copies), so
 *   id-based lookups against either collection agree exactly.
 *
 * Typed contract: generate()'s return shape is documented and checked against
 * `MazeLayout` (see src/utils/types.js, imported below) via `// @ts-check` +
 * `npm run typecheck`. GeneratorRoom/GeneratorCorridor below are this file's
 * own richer internal bookkeeping - generate() trims them down to the public
 * Room/Corridor shape before returning, so they are declared here rather
 * than in the shared contract file.
 */

/**
 * @typedef {import('./types.js').Vector3Like} Vector3Like
 * @typedef {import('./types.js').EulerLike} EulerLike
 * @typedef {import('./types.js').WallSegment} WallSegment
 * @typedef {import('./types.js').PaintingSlot} PaintingSlot
 * @typedef {import('./types.js').Room} Room
 * @typedef {import('./types.js').Corridor} Corridor
 * @typedef {import('./types.js').MazeLayout} MazeLayout
 */

/**
 * Internal room bookkeeping used while generating the maze. A superset of
 * the public {@link Room} shape returned by generate() - connections,
 * entranceCount, isEntrance, isExit, gridX and gridZ exist to build the
 * layout but are stripped before generate() returns (see generate()'s
 * `rooms:` mapping).
 *
 * @typedef {object} GeneratorRoom
 * @property {string} id
 * @property {number} gridX
 * @property {number} gridZ
 * @property {number} x
 * @property {number} z
 * @property {number} width
 * @property {number} depth
 * @property {Array<string>} connections - Ids of directly connected rooms.
 * @property {number} entranceCount
 * @property {Array<PaintingSlot>} paintings
 * @property {boolean} isEntrance
 * @property {boolean} isExit
 */

/**
 * Internal corridor bookkeeping - carries the two connected GeneratorRoom
 * object references in addition to their ids. generate() trims this to the
 * public {@link Corridor} shape (`from`/`to` ids only) before returning.
 *
 * @typedef {object} GeneratorCorridor
 * @property {string} from
 * @property {string} to
 * @property {GeneratorRoom} room1
 * @property {GeneratorRoom} room2
 */

/**
 * Constructor options for MazeGenerator. Mirrors the relevant fields of
 * maze-settings.json (see Gallery.init()'s call site) plus paintingCount,
 * which is derived from the loaded video count rather than read from
 * settings directly. All fields optional - every one has a fallback default
 * in the constructor. `seed` additionally allows `null` (not just
 * `undefined`) because the constructor explicitly special-cases it - see
 * the falsy-zero comment below.
 *
 * @typedef {object} MazeGeneratorOptions
 * @property {number} [paintingCount]
 * @property {number} [cellSize]
 * @property {number} [corridorWidth]
 * @property {number} [roomPadding]
 * @property {number | null} [seed]
 * @property {number} [minPaintingDistance]
 * @property {number} [minPaintingsPerRoom]
 * @property {number} [maxPaintingsPerRoom]
 * @property {number} [minEntrancesPerRoom]
 * @property {number} [maxEntrancesPerRoom]
 */

export class MazeGenerator {
  /**
   * @param {MazeGeneratorOptions} [config]
   */
  constructor(config = {}) {
    this.paintingCount = config.paintingCount || 9;
    this.cellSize = config.cellSize || 18;
    this.corridorWidth = config.corridorWidth || 3;
    this.roomPadding = config.roomPadding || 3;
    // Explicit null/undefined check - 0 is a valid seed and must not be
    // silently replaced because it is falsy.
    this.seed = (config.seed !== null && config.seed !== undefined) ? config.seed : Date.now();
    this.minPaintingDistance = config.minPaintingDistance || 16;

    // Room constraints
    this.minPaintingsPerRoom = config.minPaintingsPerRoom || 1;
    this.maxPaintingsPerRoom = config.maxPaintingsPerRoom || 3;
    this.minEntrancesPerRoom = config.minEntrancesPerRoom || 1;
    this.maxEntrancesPerRoom = config.maxEntrancesPerRoom || 3;

    // Initialize seeded random number generator
    this.rng = this.createSeededRNG(this.seed);

    // Data structures
    /** @type {Array<GeneratorRoom>} */
    this.rooms = [];
    /** @type {Array<WallSegment>} */
    this.walls = [];
    /** @type {Array<PaintingSlot>} */
    this.paintingSlots = [];
    /** @type {Array<GeneratorCorridor>} */
    this.corridors = [];
    this.gridSize = 0;
    /** @type {GeneratorRoom | null} */
    this.entranceRoom = null;
    /** @type {GeneratorRoom | null} */
    this.exitRoom = null;
  }

  /**
   * Seeded random number generator (Mulberry32)
   * Ensures reproducible maze generation with same seed
   */
  createSeededRNG(seed) {
    return function() {
      let t = seed += 0x6D2B79F5;
      t = Math.imul(t ^ t >>> 15, t | 1);
      t ^= t + Math.imul(t ^ t >>> 7, t | 61);
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  /**
   * Shuffle array using Fisher-Yates algorithm with seeded RNG
   */
  shuffle(array) {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  /**
   * Main generation method
   * Returns complete maze layout with walls and painting slots
   *
   * @returns {MazeLayout}
   */
  generate() {
    console.log(`Generating maze for ${this.paintingCount} paintings (seed: ${this.seed})`);

    // Phase 1: Calculate room count and grid layout
    this.calculateGridLayout();
    console.log(`Grid size: ${this.gridSize}x${this.gridSize} (${this.rooms.length} rooms)`);

    // Phase 2: Generate room connectivity (spanning tree + limited extras)
    this.generateConnectivity();

    // Phase 3: Designate entrance and exit
    this.designateEntranceAndExit();

    // Phase 4: Distribute paintings across rooms
    this.distributePaintings();

    // Phase 5: Export to wall configs
    this.exportToWallConfigs();

    // designateEntranceAndExit() (Phase 3, above) always assigns both
    // fields before generate() reaches this point - see its non-null note.
    const startRoom = /** @type {GeneratorRoom} */ (this.entranceRoom);
    const exitRoom = /** @type {GeneratorRoom} */ (this.exitRoom);

    // Calculate start position (center of entrance room)
    const centerX = startRoom.x + startRoom.width / 2;
    const centerZ = startRoom.z + startRoom.depth / 2;
    const startPosition = { x: centerX, y: 1.6, z: centerZ };

    console.log(`Generated ${this.walls.length} walls and ${this.paintingSlots.length} painting slots`);
    console.log(`Entrance: ${startRoom.id}, Exit: ${exitRoom.id}`);
    console.log(`Start position: (${centerX.toFixed(1)}, 1.6, ${centerZ.toFixed(1)})`);

    return {
      walls: this.walls,
      paintingSlots: this.paintingSlots,
      startPosition: startPosition,
      gridSize: this.gridSize,
      cellSize: this.cellSize,
      totalSize: this.gridSize * this.cellSize,
      seed: this.seed,
      entranceRoom: startRoom.id,
      exitRoom: exitRoom.id,
      // Include room and corridor data for minimap
      rooms: this.rooms.map(r => ({
        id: r.id,
        x: r.x,
        z: r.z,
        width: r.width,
        depth: r.depth,
        paintings: r.paintings
      })),
      corridors: this.corridors.map(c => ({
        from: c.from,
        to: c.to
      }))
    };
  }

  /**
   * Phase 1: Calculate grid layout based on painting count
   * Ensures enough rooms for 1-3 paintings per room
   */
  calculateGridLayout() {
    // Calculate minimum rooms needed (ceil to ensure all paintings fit)
    const minRooms = Math.ceil(this.paintingCount / this.maxPaintingsPerRoom);

    // Calculate grid size (we want a roughly square grid)
    this.gridSize = Math.ceil(Math.sqrt(minRooms));

    // Ensure we have at least enough rooms
    while (this.gridSize * this.gridSize < minRooms) {
      this.gridSize++;
    }

    // Create room grid
    this.rooms = [];
    for (let row = 0; row < this.gridSize; row++) {
      for (let col = 0; col < this.gridSize; col++) {
        const room = {
          // Deterministic from grid position alone - stable regardless of seed.
          id: `room_${row}_${col}`,
          gridX: col,
          gridZ: row,
          x: col * this.cellSize,
          z: row * this.cellSize,
          width: this.cellSize,
          depth: this.cellSize,
          connections: [], // Connected room IDs
          entranceCount: 0,
          paintings: [], // Painting slots assigned to this room
          isEntrance: false,
          isExit: false
        };
        this.rooms.push(room);
      }
    }

    console.log(`Created ${this.rooms.length} rooms for ${this.paintingCount} paintings`);
  }

  /**
   * Phase 2: Generate room connectivity
   * Uses spanning tree to ensure solvability, then adds limited extra connections
   */
  generateConnectivity() {
    this.corridors = [];

    // Step 1: Build minimum spanning tree using randomized Prim's algorithm
    // This ensures all rooms are reachable
    const connected = new Set();
    const edges = []; // Potential edges to add

    // Start with random room
    const startIndex = Math.floor(this.rng() * this.rooms.length);
    const startRoom = this.rooms[startIndex];
    connected.add(startRoom.id);

    // Add all edges from start room
    this.addPotentialEdges(startRoom, edges, connected);

    // Build spanning tree
    while (connected.size < this.rooms.length && edges.length > 0) {
      // Shuffle edges to randomize selection
      this.shuffleArray(edges);

      // Find first valid edge (connecting to unvisited room)
      let edgeIndex = -1;
      for (let i = 0; i < edges.length; i++) {
        if (!connected.has(edges[i].to.id)) {
          edgeIndex = i;
          break;
        }
      }

      if (edgeIndex === -1) break;

      const edge = edges.splice(edgeIndex, 1)[0];

      // Check entrance limits before adding connection
      if (edge.from.entranceCount < this.maxEntrancesPerRoom &&
          edge.to.entranceCount < this.maxEntrancesPerRoom) {
        this.createCorridor(edge.from, edge.to);
        connected.add(edge.to.id);
        this.addPotentialEdges(edge.to, edges, connected);
      }
    }

    // Step 2: Add extra connections for variety (respecting max entrances)
    this.addExtraConnections();

    console.log(`Created ${this.corridors.length} corridors`);
    this.logConnectivityStats();
  }

  /**
   * Add potential edges from a room to unconnected neighbors
   */
  addPotentialEdges(room, edges, connected) {
    const neighbors = this.getNeighbors(room);
    for (const neighbor of neighbors) {
      if (!connected.has(neighbor.id)) {
        edges.push({ from: room, to: neighbor });
      }
    }
  }

  /**
   * Get adjacent rooms (north, south, east, west)
   */
  getNeighbors(room) {
    const neighbors = [];
    const directions = [
      { dx: 0, dz: -1 }, // north
      { dx: 0, dz: 1 },  // south
      { dx: 1, dz: 0 },  // east
      { dx: -1, dz: 0 }  // west
    ];

    for (const dir of directions) {
      const nx = room.gridX + dir.dx;
      const nz = room.gridZ + dir.dz;

      if (nx >= 0 && nx < this.gridSize && nz >= 0 && nz < this.gridSize) {
        const neighbor = this.rooms.find(r => r.gridX === nx && r.gridZ === nz);
        if (neighbor) {
          neighbors.push(neighbor);
        }
      }
    }

    return neighbors;
  }

  /**
   * Add extra connections while respecting max entrances per room
   */
  addExtraConnections() {
    // Get all possible edges not yet connected
    const possibleEdges = [];

    for (const room of this.rooms) {
      if (room.entranceCount >= this.maxEntrancesPerRoom) continue;

      const neighbors = this.getNeighbors(room);
      for (const neighbor of neighbors) {
        // Check if not already connected and neighbor has room for more entrances
        if (!room.connections.includes(neighbor.id) &&
            neighbor.entranceCount < this.maxEntrancesPerRoom) {
          // Avoid duplicate edges
          const edgeKey = [room.id, neighbor.id].sort().join('-');
          if (!possibleEdges.some(e => [e.from.id, e.to.id].sort().join('-') === edgeKey)) {
            possibleEdges.push({ from: room, to: neighbor });
          }
        }
      }
    }

    // Shuffle and add some extra connections (about 20% of possible)
    this.shuffleArray(possibleEdges);
    const extraCount = Math.floor(possibleEdges.length * 0.2);

    for (let i = 0; i < extraCount && i < possibleEdges.length; i++) {
      const edge = possibleEdges[i];
      // Double-check entrance limits
      if (edge.from.entranceCount < this.maxEntrancesPerRoom &&
          edge.to.entranceCount < this.maxEntrancesPerRoom) {
        this.createCorridor(edge.from, edge.to);
      }
    }
  }

  /**
   * Shuffle array in place
   */
  shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }

  /**
   * Create corridor between two rooms
   */
  createCorridor(room1, room2) {
    // Add to connections
    if (!room1.connections.includes(room2.id)) {
      room1.connections.push(room2.id);
      room1.entranceCount++;
    }
    if (!room2.connections.includes(room1.id)) {
      room2.connections.push(room1.id);
      room2.entranceCount++;
    }

    // Store corridor data
    this.corridors.push({
      from: room1.id,
      to: room2.id,
      room1: room1,
      room2: room2
    });
  }

  /**
   * Log connectivity statistics
   */
  logConnectivityStats() {
    const entranceCounts = this.rooms.map(r => r.entranceCount);
    const min = Math.min(...entranceCounts);
    const max = Math.max(...entranceCounts);
    const avg = entranceCounts.reduce((a, b) => a + b, 0) / entranceCounts.length;

    console.log(`Entrance stats - Min: ${min}, Max: ${max}, Avg: ${avg.toFixed(1)}`);

    // Verify all rooms meet constraints
    const violations = this.rooms.filter(r =>
      r.entranceCount < this.minEntrancesPerRoom ||
      r.entranceCount > this.maxEntrancesPerRoom
    );

    if (violations.length > 0) {
      console.warn(`${violations.length} rooms violate entrance constraints`);
    }
  }

  /**
   * Phase 3: Designate entrance and exit rooms
   * Places them at opposite corners/edges for maximum path length
   */
  designateEntranceAndExit() {
    // Find rooms at opposite corners. `.filter(Boolean)` drops the
    // `undefined` *entries* at runtime, so the cast is accurate per
    // element - it says nothing about the array's *length*, though. A
    // small gridSize can mean fewer than 4 corner rooms are ever found
    // (some of the four find() calls below return undefined and get
    // filtered out), so corners[entranceIndex]/corners[exitIndex]/
    // corners[corners.length - 1 - entranceIndex] below can still read out
    // of bounds - runtime `undefined`, invisible to this type without
    // `noUncheckedIndexedAccess` (not enabled here). That's exactly what
    // the `if (!this.entranceRoom)`/`if (!this.exitRoom)` fallbacks a few
    // lines down are for: live defensive code for that case, not the dead
    // code this cast's "always GeneratorRoom" view might otherwise suggest.
    const corners = /** @type {Array<GeneratorRoom>} */ ([
      this.rooms.find(r => r.gridX === 0 && r.gridZ === 0),
      this.rooms.find(r => r.gridX === this.gridSize - 1 && r.gridZ === 0),
      this.rooms.find(r => r.gridX === 0 && r.gridZ === this.gridSize - 1),
      this.rooms.find(r => r.gridX === this.gridSize - 1 && r.gridZ === this.gridSize - 1)
    ].filter(Boolean));

    // Randomly select entrance from one pair of opposite corners
    const entranceIndex = Math.floor(this.rng() * 2);
    this.entranceRoom = corners[entranceIndex];

    // Exit is the opposite corner
    const exitIndex = entranceIndex === 0 ? 3 : (entranceIndex === 1 ? 2 : (entranceIndex === 2 ? 1 : 0));
    this.exitRoom = corners[exitIndex] || corners[corners.length - 1 - entranceIndex];

    // Fallback if corners aren't available
    if (!this.entranceRoom) this.entranceRoom = this.rooms[0];
    if (!this.exitRoom) this.exitRoom = this.rooms[this.rooms.length - 1];

    // Both fields are always assigned above (falling back to
    // this.rooms[0]/this.rooms[this.rooms.length - 1] when no corner room
    // is found) - non-null here in every realistic case (paintingCount >= 1,
    // guaranteeing at least one room). A paintingCount of 0 would produce
    // zero rooms and leave these unset - a pre-existing, unrelated edge
    // case in calculateGridLayout()'s room-count math; out of scope for
    // this story (fixing it would change generation behaviour, not just
    // annotate it). Read through locals for the rest of this method since
    // this.verifySolvability()/this.ensureSolvability() below are method
    // calls the checker can't prove leave these fields untouched.
    const entranceRoom = /** @type {GeneratorRoom} */ (this.entranceRoom);
    const exitRoom = /** @type {GeneratorRoom} */ (this.exitRoom);

    entranceRoom.isEntrance = true;
    exitRoom.isExit = true;

    // Verify path exists between entrance and exit
    if (!this.verifySolvability()) {
      console.warn('Maze not solvable, regenerating connections...');
      this.ensureSolvability();
    }

    console.log(`Entrance: ${entranceRoom.id}, Exit: ${exitRoom.id}`);
  }

  /**
   * Verify maze is solvable using BFS
   */
  verifySolvability() {
    // Called only from designateEntranceAndExit(), after entranceRoom/
    // exitRoom have been assigned - see the non-null note there.
    const entranceRoom = /** @type {GeneratorRoom} */ (this.entranceRoom);
    const exitRoom = /** @type {GeneratorRoom} */ (this.exitRoom);

    const visited = new Set();
    const queue = [entranceRoom];
    visited.add(entranceRoom.id);

    while (queue.length > 0) {
      // queue.length > 0 was just checked, so shift() always returns a
      // real element here - the checker can't correlate the two.
      const current = /** @type {GeneratorRoom} */ (queue.shift());

      if (current.id === exitRoom.id) {
        return true;
      }

      for (const connectionId of current.connections) {
        if (!visited.has(connectionId)) {
          visited.add(connectionId);
          const connectedRoom = this.rooms.find(r => r.id === connectionId);
          if (connectedRoom) {
            queue.push(connectedRoom);
          }
        }
      }
    }

    return false;
  }

  /**
   * Ensure maze is solvable by adding necessary connections
   */
  ensureSolvability() {
    // Called only from designateEntranceAndExit(), after entranceRoom/
    // exitRoom have been assigned - see the non-null note there.
    const entranceRoom = /** @type {GeneratorRoom} */ (this.entranceRoom);
    const exitRoom = /** @type {GeneratorRoom} */ (this.exitRoom);

    // Find path using BFS and track which rooms are reachable from entrance
    const reachableFromEntrance = new Set();
    const queue = [entranceRoom];
    reachableFromEntrance.add(entranceRoom.id);

    while (queue.length > 0) {
      const current = /** @type {GeneratorRoom} */ (queue.shift());
      for (const connectionId of current.connections) {
        if (!reachableFromEntrance.has(connectionId)) {
          reachableFromEntrance.add(connectionId);
          const connectedRoom = this.rooms.find(r => r.id === connectionId);
          if (connectedRoom) queue.push(connectedRoom);
        }
      }
    }

    // If exit not reachable, create path
    if (!reachableFromEntrance.has(exitRoom.id)) {
      // Find closest reachable room to exit
      /** @type {GeneratorRoom | null} */
      let closestRoom = null;
      let closestDist = Infinity;

      for (const roomId of reachableFromEntrance) {
        // NOT because this.rooms.find() already succeeded once for this
        // id above - reachableFromEntrance.add(connectionId) (a few lines
        // up) runs unconditionally, before that find() and regardless of
        // whether it succeeds; only the queue.push() is gated on it. The
        // real reason this always resolves: every id that ever enters a
        // room's `.connections` array is written by createCorridor()
        // (called from generateConnectivity()/addExtraConnections()/
        // createPathBetween()), which only ever receives real
        // GeneratorRoom objects drawn from this.rooms and pushes their
        // `.id` - so any id reachableFromEntrance picks up by walking
        // `current.connections` already names a room that exists in
        // this.rooms, independent of this loop's own add()/find() order.
        const room = /** @type {GeneratorRoom} */ (this.rooms.find(r => r.id === roomId));
        const dist = Math.abs(room.gridX - exitRoom.gridX) +
                     Math.abs(room.gridZ - exitRoom.gridZ);
        if (dist < closestDist) {
          closestDist = dist;
          closestRoom = room;
        }
      }

      // Create path from closest reachable room to exit
      if (closestRoom) {
        this.createPathBetween(closestRoom, exitRoom);
      }
    }
  }

  /**
   * Create a path between two rooms through intermediate rooms
   */
  createPathBetween(from, to) {
    let current = from;

    while (current.id !== to.id) {
      const dx = to.gridX - current.gridX;
      const dz = to.gridZ - current.gridZ;

      // Move towards target
      let nextX = current.gridX;
      let nextZ = current.gridZ;

      if (Math.abs(dx) >= Math.abs(dz) && dx !== 0) {
        nextX += dx > 0 ? 1 : -1;
      } else if (dz !== 0) {
        nextZ += dz > 0 ? 1 : -1;
      }

      const nextRoom = this.rooms.find(r => r.gridX === nextX && r.gridZ === nextZ);
      if (nextRoom && !current.connections.includes(nextRoom.id)) {
        this.createCorridor(current, nextRoom);
      }

      if (nextRoom) {
        current = nextRoom;
      } else {
        break;
      }
    }
  }

  /**
   * Phase 4: Distribute paintings across rooms
   * Each room gets 1-3 paintings
   */
  distributePaintings() {
    this.paintingSlots = [];
    const frameStyles = ['gold', 'dark-wood', 'metal'];
    let paintingsRemaining = this.paintingCount;
    let slotIndex = 0;

    // Calculate paintings per room
    const paintingsPerRoom = this.calculatePaintingsPerRoom();

    // Calculate corridor segments to avoid placing paintings on entrances
    const corridorSegments = this.calculateCorridorSegments();

    for (let i = 0; i < this.rooms.length && paintingsRemaining > 0; i++) {
      const room = this.rooms[i];
      const numPaintings = Math.min(paintingsPerRoom[i], paintingsRemaining);

      // Get available walls (not blocked by corridors)
      const availableWalls = this.getAvailableWalls(room, corridorSegments);

      // Place paintings on available walls
      const wallsToUse = availableWalls.slice(0, numPaintings);

      for (const wall of wallsToUse) {
        const frameStyle = frameStyles[slotIndex % frameStyles.length];

        const slot = {
          // Stable, deterministic identity - see "Identity contract" above.
          id: `${room.id}_${wall.name}`,
          position: wall.position,
          rotation: wall.rotation,
          wall: wall.name,
          roomId: room.id,
          frameStyle: frameStyle,
          clearanceRadius: 8
        };

        this.paintingSlots.push(slot);
        room.paintings.push(slot);
        slotIndex++;
        paintingsRemaining--;
      }
    }

    console.log(`Distributed ${this.paintingSlots.length} paintings across ${this.rooms.length} rooms`);
  }

  /**
   * Calculate how many paintings each room should have
   */
  calculatePaintingsPerRoom() {
    const totalRooms = this.rooms.length;
    const paintingsPerRoom = new Array(totalRooms).fill(0);
    let remaining = this.paintingCount;

    // First pass: give each room minimum paintings
    for (let i = 0; i < totalRooms && remaining > 0; i++) {
      const toAdd = Math.min(this.minPaintingsPerRoom, remaining);
      paintingsPerRoom[i] = toAdd;
      remaining -= toAdd;
    }

    // Second pass: distribute remaining paintings randomly up to max
    const roomIndices = [...Array(totalRooms).keys()];
    this.shuffleArray(roomIndices);

    for (const i of roomIndices) {
      if (remaining <= 0) break;

      const canAdd = Math.min(
        this.maxPaintingsPerRoom - paintingsPerRoom[i],
        remaining
      );

      if (canAdd > 0) {
        const toAdd = Math.floor(this.rng() * canAdd) + 1;
        paintingsPerRoom[i] += Math.min(toAdd, remaining);
        remaining -= Math.min(toAdd, remaining);
      }
    }

    // If still remaining, force add to rooms with space
    while (remaining > 0) {
      for (let i = 0; i < totalRooms && remaining > 0; i++) {
        if (paintingsPerRoom[i] < this.maxPaintingsPerRoom) {
          paintingsPerRoom[i]++;
          remaining--;
        }
      }
      // Safety break if we can't place all paintings
      if (paintingsPerRoom.every(p => p >= this.maxPaintingsPerRoom)) {
        console.warn(`Cannot place all paintings, ${remaining} remaining`);
        break;
      }
    }

    return paintingsPerRoom;
  }

  /**
   * Get available walls for paintings (excluding corridor entrances)
   *
   * @param {GeneratorRoom} room
   * @param {Map<string, { width: number, center: number }>} corridorSegments
   * @returns {Array<{ name: string, position: Vector3Like, rotation: EulerLike }>}
   */
  getAvailableWalls(room, corridorSegments) {
    const walls = [];
    /** @type {readonly ['north', 'south', 'east', 'west']} */
    const wallOptions = ['north', 'south', 'east', 'west'];

    const centerX = room.x + room.width / 2;
    const centerZ = room.z + room.depth / 2;
    const eyeLevel = 1.6;
    const offset = 0.5;

    for (const wallName of wallOptions) {
      const key = `${room.id}_${wallName}`;

      // Skip walls with corridor entrances
      if (corridorSegments.has(key)) continue;

      // Typed on the declaration (rather than cast where used below) so
      // each branch's object literal is checked against Vector3Like/
      // EulerLike individually - a typo'd field name in any one case is
      // caught right there, instead of only being caught (or not) via a
      // looser check against whatever union of shapes the four branches
      // happen to infer.
      /** @type {Vector3Like} */
      let position;
      /** @type {EulerLike} */
      let rotation;

      switch (wallName) {
        case 'north':
          position = { x: centerX, y: eyeLevel, z: room.z + offset };
          rotation = { x: 0, y: 0, z: 0 };
          break;
        case 'south':
          position = { x: centerX, y: eyeLevel, z: room.z + room.depth - offset };
          rotation = { x: 0, y: Math.PI, z: 0 };
          break;
        case 'east':
          position = { x: room.x + room.width - offset, y: eyeLevel, z: centerZ };
          rotation = { x: 0, y: -Math.PI / 2, z: 0 };
          break;
        case 'west':
          position = { x: room.x + offset, y: eyeLevel, z: centerZ };
          rotation = { x: 0, y: Math.PI / 2, z: 0 };
          break;
      }

      walls.push({ name: wallName, position, rotation });
    }

    // Shuffle walls for variety
    this.shuffleArray(walls);
    return walls;
  }

  /**
   * Calculate corridor segments to exclude from walls
   */
  calculateCorridorSegments() {
    const segments = new Map();

    for (const corridor of this.corridors) {
      const { room1, room2 } = corridor;

      const centerX1 = room1.x + room1.width / 2;
      const centerZ1 = room1.z + room1.depth / 2;
      const centerX2 = room2.x + room2.width / 2;
      const centerZ2 = room2.z + room2.depth / 2;

      const dx = centerX2 - centerX1;
      const dz = centerZ2 - centerZ1;

      // Determine which wall the corridor goes through
      let wall1, wall2;
      if (Math.abs(dx) > Math.abs(dz)) {
        wall1 = dx > 0 ? 'east' : 'west';
        wall2 = dx > 0 ? 'west' : 'east';
      } else {
        wall1 = dz > 0 ? 'south' : 'north';
        wall2 = dz > 0 ? 'north' : 'south';
      }

      const key1 = `${room1.id}_${wall1}`;
      const key2 = `${room2.id}_${wall2}`;

      segments.set(key1, {
        width: this.corridorWidth,
        center: Math.abs(dx) > Math.abs(dz) ? centerZ1 : centerX1
      });
      segments.set(key2, {
        width: this.corridorWidth,
        center: Math.abs(dx) > Math.abs(dz) ? centerZ2 : centerX2
      });
    }

    return segments;
  }

  /**
   * Phase 5: Export to wall configs
   */
  exportToWallConfigs() {
    this.walls = [];
    const wallHeight = 4;
    const wallDepth = 0.3;

    const corridorSegments = this.calculateCorridorSegments();

    for (const room of this.rooms) {
      this.addRoomWalls(room, wallHeight, wallDepth, corridorSegments);
    }

    console.log(`Exported ${this.walls.length} wall segments`);
  }

  /**
   * Add walls for a room, accounting for corridors
   */
  addRoomWalls(room, wallHeight, wallDepth, corridorSegments) {
    const centerY = wallHeight / 2;
    const directions = ['north', 'south', 'east', 'west'];

    for (const direction of directions) {
      this.addWall(room, direction, centerY, wallDepth, corridorSegments);
    }
  }

  /**
   * Add a single wall, splitting around corridors if needed
   *
   * @param {GeneratorRoom} room
   * @param {string} direction - One of 'north' | 'south' | 'east' | 'west'.
   * @param {number} centerY
   * @param {number} wallDepth
   * @param {Map<string, { width: number, center: number }>} corridorSegments
   */
  addWall(room, direction, centerY, wallDepth, corridorSegments) {
    const key = `${room.id}_${direction}`;
    const corridor = corridorSegments.get(key);

    // Typed on the declaration (see getAvailableWalls for the same
    // reasoning) so each branch below is checked against Vector3Like/
    // EulerLike individually rather than via a looser post-hoc cast.
    /** @type {Vector3Like} */
    let position;
    /** @type {EulerLike} */
    let rotation;
    /** @type {{ width: number, height: number, depth: number } | undefined} */
    let dimensions;

    switch (direction) {
      case 'north':
        position = { x: room.x + room.width / 2, y: centerY, z: room.z };
        rotation = { x: 0, y: 0, z: 0 };

        if (corridor) {
          this.addSplitWall(position, rotation, room.width, 4, wallDepth, this.corridorWidth, true);
        } else {
          dimensions = { width: room.width, height: 4, depth: wallDepth };
          this.walls.push({ position, rotation, dimensions });
        }
        break;

      case 'south':
        position = { x: room.x + room.width / 2, y: centerY, z: room.z + room.depth };
        rotation = { x: 0, y: 0, z: 0 };

        if (corridor) {
          this.addSplitWall(position, rotation, room.width, 4, wallDepth, this.corridorWidth, true);
        } else {
          dimensions = { width: room.width, height: 4, depth: wallDepth };
          this.walls.push({ position, rotation, dimensions });
        }
        break;

      case 'east':
        position = { x: room.x + room.width, y: centerY, z: room.z + room.depth / 2 };
        rotation = { x: 0, y: Math.PI / 2, z: 0 };

        if (corridor) {
          this.addSplitWall(position, rotation, room.depth, 4, wallDepth, this.corridorWidth, false);
        } else {
          dimensions = { width: room.depth, height: 4, depth: wallDepth };
          this.walls.push({ position, rotation, dimensions });
        }
        break;

      case 'west':
        position = { x: room.x, y: centerY, z: room.z + room.depth / 2 };
        rotation = { x: 0, y: Math.PI / 2, z: 0 };

        if (corridor) {
          this.addSplitWall(position, rotation, room.depth, 4, wallDepth, this.corridorWidth, false);
        } else {
          dimensions = { width: room.depth, height: 4, depth: wallDepth };
          this.walls.push({ position, rotation, dimensions });
        }
        break;
    }
  }

  /**
   * Add wall segments split by corridor opening
   *
   * @param {Vector3Like} basePosition
   * @param {EulerLike} rotation
   * @param {number} totalWidth
   * @param {number} height
   * @param {number} depth
   * @param {number} corridorWidth
   * @param {boolean} isHorizontal
   */
  addSplitWall(basePosition, rotation, totalWidth, height, depth, corridorWidth, isHorizontal) {
    const segmentWidth = (totalWidth - corridorWidth) / 2;

    if (segmentWidth < 0.5) return;

    // Left/Top segment
    const offset1 = -(totalWidth / 2 - segmentWidth / 2);
    this.walls.push({
      position: {
        x: basePosition.x + (isHorizontal ? offset1 : 0),
        y: basePosition.y,
        z: basePosition.z + (isHorizontal ? 0 : offset1)
      },
      rotation: rotation,
      dimensions: { width: segmentWidth, height: height, depth: depth }
    });

    // Right/Bottom segment
    const offset2 = (totalWidth / 2 - segmentWidth / 2);
    this.walls.push({
      position: {
        x: basePosition.x + (isHorizontal ? offset2 : 0),
        y: basePosition.y,
        z: basePosition.z + (isHorizontal ? 0 : offset2)
      },
      rotation: rotation,
      dimensions: { width: segmentWidth, height: height, depth: depth }
    });
  }

  /**
   * Validate painting spacing
   * Ensures no two paintings are within minimum distance
   */
  validatePaintingSpacing() {
    let valid = true;

    for (let i = 0; i < this.paintingSlots.length; i++) {
      for (let j = i + 1; j < this.paintingSlots.length; j++) {
        const slot1 = this.paintingSlots[i];
        const slot2 = this.paintingSlots[j];

        const distance = Math.sqrt(
          Math.pow(slot2.position.x - slot1.position.x, 2) +
          Math.pow(slot2.position.z - slot1.position.z, 2)
        );

        if (distance < this.minPaintingDistance) {
          console.warn(`Painting ${i} and ${j} too close: ${distance.toFixed(2)} units (min: ${this.minPaintingDistance})`);
          valid = false;
        }
      }
    }

    if (valid) {
      console.log('✓ All paintings meet minimum spacing requirement');
    }
    return valid;
  }

  /**
   * Get maze statistics for debugging
   */
  getStats() {
    return {
      roomCount: this.rooms.length,
      corridorCount: this.corridors.length,
      paintingCount: this.paintingSlots.length,
      gridSize: this.gridSize,
      entranceRoom: this.entranceRoom?.id,
      exitRoom: this.exitRoom?.id,
      entranceCounts: this.rooms.map(r => ({ id: r.id, entrances: r.entranceCount })),
      paintingCounts: this.rooms.map(r => ({ id: r.id, paintings: r.paintings.length }))
    };
  }
}
